// tweak-chrome — the isolated "tweak" surface (tweak-activity). Host chrome AROUND the
// unchanged runner: the teacher's game runs on the read-only client-runner (right), with
// the READ-ONLY SOURCE CODE beside it (left), the tweakable values editable INLINE in the
// code as type-aware widgets (number box / colour swatch / on-off toggle / dropdown / text).
// There is NO side control panel and NO sliders — a student changes a value where it lives
// in the program, so the change has context. Editing a value re-runs the program instantly,
// client-side, by substituting the chosen values into the runner's load.files
// (substitute-constants.js) and re-running via editor-core's existing load()/run() — NO
// runner-contract change, editor-core untouched.
//
//   wire(ctx) — ctx = { core, payload, trinketId, clientsideMode, isOwner, editorApi, doc }
//
// #editor (Ace) is constructed-but-hidden by main.js; this surface renders its own code
// pane into the editor host instead. A completion is recorded on the FIRST successful run
// (the runner reaching `running` for a game, or `finished` for a console program, after a
// clean load) — best-effort, same-origin; a teacher's 403 is swallowed silently (mirrors
// readcode/worked-example).

import { substituteConstants } from './substitute-constants.js';
import { buildTweakCodeModel } from './tweak-code-model.js';
// Per-line syntax highlighter (a JS port of src/islands/shared/highlight-python.ts kept in
// sync) — pure, no DOM. Used to render the read-only code around each inline widget.
import { highlightPythonLine } from './highlight-python.js';
import { mountPreviewStrip } from './preview-strip.js';

var RERUN_DEBOUNCE_MS = 200;

// Read + parse the server-embedded controls config (already '<'-escaped by the handler).
function readConfig(doc) {
  var el = doc.getElementById('tweak-config');
  if (!el) { return null; }
  try {
    var cfg = JSON.parse(el.textContent || '{}');
    if (!cfg || !Array.isArray(cfg.controls)) { return null; }
    return cfg;
  } catch (e) { return null; }
}

// The stored default for a control, coerced to the widget's value shape.
function defaultValue(control) {
  var d = control.default;
  switch (control.type) {
    case 'toggle':
      return !!d;
    case 'slider':
    case 'number': {
      var n = Number(d);
      return isFinite(n) ? n : 0;
    }
    case 'colour':
      return (typeof d === 'string' && /^#?[0-9a-fA-F]{3,6}$/.test(d.trim()))
        ? (d.trim().charAt(0) === '#' ? d.trim() : '#' + d.trim())
        : '#000000';
    case 'choice':
    default:
      return d == null ? '' : String(d);
  }
}

// The main file's source text (prefer main.py, else the first file) — the code we render.
function mainSource(files) {
  if (!Array.isArray(files) || !files.length) { return ''; }
  var m = files.filter(function (f) { return f && f.name === 'main.py'; })[0] || files[0];
  return (m && m.content) || '';
}

export function wire(ctx) {
  var doc = ctx.doc;
  var core = ctx.core;
  var cfg = readConfig(doc);
  if (!cfg) { return; }

  // teacher-preview-split (tasks 9.1/9.3): SERVER-RESOLVED preview mode. `cfg.preview` was
  // set only after the route's pre-handler confirmed `isCourseStaff` (R17), so a student who
  // opens a shared or bookmarked preview-flagged URL sees no strip and keeps persisting
  // exactly as today.
  var previewMode = cfg.preview === true;
  // one-tab-same-tab-launches (Trello bD1oe35m): the activity top bar's preview chip and "Back to
  // class" replace the strip, so it is mounted only when the bar is absent (card 2's guard).
  if (!doc.getElementById('surface-topbar-back')) {
    mountPreviewStrip(doc, { active: previewMode, exit: 'reload' });
  }

  // The way out of an open-ended activity. Tweak has NO finish line: `recordCompletion` fires on
  // the FIRST successful run, before the student has tweaked anything, so a countdown hung off
  // it would try to take the page away at the START of the exercise. one-tab-same-tab-launches:
  // tweak now opens in the classroom's own tab and the activity top bar's Back is the way out,
  // so the standing close-the-tab control is gone.

  var resetBtn = doc.getElementById('tweak-reset');
  var host = doc.querySelector('.editor-host') || doc.querySelector('.editor-pane');
  if (!host) { return; }

  var controls = cfg.controls;
  // The ORIGINAL source files (the teacher's game) — the substitution baseline. Never
  // mutated; every re-run substitutes fresh from this.
  var baseFiles = Array.isArray(ctx.payload && ctx.payload.files) ? ctx.payload.files : [];
  var mode = (ctx.payload && ctx.payload.mode) || ctx.clientsideMode || '';
  var assetBaseUrl = (ctx.payload && ctx.payload.assetBaseUrl) || '';
  var assetManifest = (ctx.payload && ctx.payload.assetManifest) || [];
  var source = mainSource(baseFiles);

  // Current control values (seeded from defaults).
  var values = {};
  controls.forEach(function (c) { if (c && c.name) { values[c.name] = defaultValue(c); } });

  var ready = false;
  var rerunTimer = null;
  var completed = false;

  // Substitute the current values into the baseline files, load them into editor-core,
  // and run. Only once the runner is ready (run() is a no-op otherwise).
  function applyAndRun() {
    if (!ready) { return; }
    var files = substituteConstants(baseFiles, controls, values);
    var main = files.filter(function (f) { return f && f.name === 'main.py'; })[0] || files[0];
    core.load({
      source: main ? main.content : '',
      files: files,
      assetBaseUrl: assetBaseUrl,
      assetManifest: assetManifest,
      mode: mode
    });
    core.run();
  }

  // Debounced re-run (single-in-flight): a value dragged/typed re-runs at most every
  // ~200 ms rather than dozens of times a second (strobe / worker wedge).
  function scheduleRerun() {
    if (rerunTimer) { clearTimeout(rerunTimer); }
    rerunTimer = setTimeout(function () { rerunTimer = null; applyAndRun(); }, RERUN_DEBOUNCE_MS);
  }

  // ── completion: record once on the first successful run ────────────────────
  function recordCompletion() {
    if (completed) { return; }
    completed = true;
    // teacher-preview-split (task 9.2 / D6): completion affordances stay LIVE in preview
    // mode — the tick flips — but record NOTHING. This is an explicit client guard, not a
    // reliance on the server's roster 403.
    if (previewMode) { return; }
    var url = '/api/courses/' + cfg.courseId + '/lessons/' + cfg.lessonId
            + '/materials/' + cfg.materialId + '/complete';
    // per-class-material-progress: stamp the class cohort key (threaded via the launch URL →
    // cfg) when present so this completion is per-class; a non-class launch sends an empty body.
    var body = cfg.linkedCourseId ? JSON.stringify({ linkedCourseId: cfg.linkedCourseId }) : '{}';
    try {
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: body
      }).catch(function () { /* best-effort; a teacher's 403 is expected + silent */ });
    } catch (e) { /* never surface a completion failure */ }
  }

  // ── inline value widgets ────────────────────────────────────────────────────
  // Size a text/number input to roughly its content so it reads as a snug inline chip.
  function sizeToContent(input) {
    var len = String(input.value == null ? '' : input.value).length;
    // Width = character content + a fixed allowance for the chip's own padding + border
    // (the box is border-box, so a bare `ch` count clips the last glyph). See the twin
    // sizing in pc-tweak-dialog.ts's preview — keep the two in sync.
    input.style.width = 'calc(' + Math.max(len + 1, 2) + 'ch + 14px)';
  }

  // Build the inline widget element for a control, wired to update `values` + re-run.
  function makeWidget(control) {
    var name = control.name;
    var type = control.type;
    var input;

    if (type === 'toggle') {
      input = doc.createElement('select');
      input.className = 'tweak-w tweak-w-toggle';
      // code-a-game-browser-fixes (G): the same accessible name the number/colour/text fields carry.
      input.setAttribute('aria-label', control.label || name);
      [['true', 'True'], ['false', 'False']].forEach(function (pair) {
        var o = doc.createElement('option');
        o.value = pair[0];
        o.textContent = pair[1];
        if ((pair[0] === 'true') === !!values[name]) { o.selected = true; }
        input.appendChild(o);
      });
      input.addEventListener('change', function () {
        values[name] = input.value === 'true';
        scheduleRerun();
      });
      return input;
    }

    if (type === 'colour') {
      input = doc.createElement('input');
      input.type = 'color';
      input.className = 'tweak-w tweak-w-color';
      input.value = values[name];
      input.setAttribute('aria-label', control.label || name);
      input.addEventListener('input', function () {
        values[name] = input.value;
        scheduleRerun();
      });
      return input;
    }

    if (type === 'choice' && Array.isArray(control.options) && control.options.length) {
      input = doc.createElement('select');
      input.className = 'tweak-w tweak-w-choice';
      input.setAttribute('aria-label', control.label || name);
      control.options.forEach(function (opt) {
        var o = doc.createElement('option');
        o.value = opt;
        o.textContent = opt;
        if (opt === values[name]) { o.selected = true; }
        input.appendChild(o);
      });
      input.addEventListener('change', function () {
        values[name] = input.value;
        scheduleRerun();
      });
      return input;
    }

    if (type === 'number' || type === 'slider') {
      input = doc.createElement('input');
      input.type = 'number';
      input.className = 'tweak-w tweak-w-num';
      if (isFinite(Number(control.min))) { input.min = String(Number(control.min)); }
      if (isFinite(Number(control.max))) { input.max = String(Number(control.max)); }
      if (isFinite(Number(control.step)) && Number(control.step) > 0) { input.step = String(Number(control.step)); }
      input.value = String(values[name]);
      input.setAttribute('aria-label', control.label || name);
      sizeToContent(input);
      // While typing, only resize the chip — do NOT re-run. A re-run calls core.load(),
      // which rebuilds Ace's buffers and steals focus from the field mid-type (blanking
      // it). Commit the value + re-run on `change` (blur / Enter) instead; clamp to
      // min/max and restore the last good value if the field was left empty/invalid.
      input.addEventListener('input', function () { sizeToContent(input); });
      input.addEventListener('change', function () {
        var n = Number(input.value);
        if (input.value === '' || !isFinite(n)) { input.value = String(values[name]); sizeToContent(input); return; }
        var mn = Number(control.min), mx = Number(control.max);
        if (isFinite(mn) && n < mn) { n = mn; }
        if (isFinite(mx) && n > mx) { n = mx; }
        input.value = String(n);
        sizeToContent(input);
        values[name] = n;
        scheduleRerun();
      });
      return input;
    }

    // choice without options, text, or an unknown type → a free-text field (never crash).
    input = doc.createElement('input');
    input.type = 'text';
    input.className = 'tweak-w tweak-w-text';
    input.value = String(values[name] == null ? '' : values[name]);
    input.setAttribute('aria-label', control.label || name);
    sizeToContent(input);
    // Resize live while typing; commit + re-run on `change` (blur / Enter) so a
    // mid-type reload never steals focus and blanks the field (see the number branch).
    input.addEventListener('input', function () { sizeToContent(input); });
    input.addEventListener('change', function () {
      values[name] = input.value;
      sizeToContent(input);
      scheduleRerun();
    });
    return input;
  }

  // ── code pane ────────────────────────────────────────────────────────────────
  var codeEl = doc.createElement('div');
  codeEl.className = 'tweak-code';
  codeEl.setAttribute('aria-label', 'Program code — change the boxed values');
  host.appendChild(codeEl);

  function renderCode() {
    var model = buildTweakCodeModel(source, controls, highlightPythonLine);
    codeEl.textContent = '';
    model.forEach(function (row, i) {
      var line = doc.createElement('div');
      line.className = 'tweak-line' + (row.kind === 'control' ? ' is-control' : '');

      var n = doc.createElement('span');
      n.className = 'n';
      n.textContent = String(i + 1);

      var s = doc.createElement('span');
      s.className = 's';
      if (row.kind === 'control') {
        var before = doc.createElement('span');
        before.innerHTML = row.before; // highlightPythonLine output — escaped, safe
        var after = doc.createElement('span');
        after.innerHTML = row.after;
        s.appendChild(before);
        s.appendChild(makeWidget(row.control));
        s.appendChild(after);
      } else {
        s.innerHTML = row.html;
      }

      line.appendChild(n);
      line.appendChild(s);
      codeEl.appendChild(line);
    });
  }

  renderCode();

  // Reset: restore every control to its stored default, re-render the code (so widgets
  // reflect the defaults), re-run.
  if (resetBtn) {
    resetBtn.addEventListener('click', function () {
      controls.forEach(function (c) { if (c && c.name) { values[c.name] = defaultValue(c); } });
      renderCode();
      applyAndRun();
    });
  }

  // First run + completion: run once the runner is ready, and record the completion the
  // first time it reaches `running` (games loop forever) or `finished` (console mode).
  core.on('ready', function () {
    ready = true;
    applyAndRun();
  });
  core.on('state', function (state) {
    if (state === 'running' || state === 'finished') { recordCompletion(); }
  });
}
